/** Segmente und Listenzeilen der Zeitverteilung (F-28) - Farben zentral. */
import { NO_AREA_COLOR } from '@/domain/areaColors';
import { OHNE_BEREICH, stunden, type ZeitVerteilung } from '@/domain/zeit/verteilung';
import type { Area, ID } from '@/domain/types';
import type { RingSegment } from '@/modules/zeit/ZeitRing';

export const FARBE_SCHLAF = 'var(--color-zeit-schlaf)';
export const FARBE_UNVERPLANT = 'var(--color-zeit-unverplant)';
const FARBE_SONSTIGES = 'var(--color-line-strong)';
/** Mehr Segmente sind im Ring nicht mehr zu unterscheiden (Vorschlag). */
const MAX_BEREICHE = 6;

export interface Zeile extends RingSegment {
  areaId?: ID | null;
  art: 'bereich' | 'schlaf' | 'unverplant';
  prozent: number;
}

export function zeilen(v: ZeitVerteilung, areas: Map<ID, Area>): Zeile[] {
  const basis = v.verplant + v.schlaf + v.unverplant || 1;
  const z = (s: Omit<Zeile, 'prozent' | 'text'>): Zeile => ({ ...s, prozent: Math.round((s.minuten / basis) * 100), text: stunden(s.minuten) });
  // Bereiche mit Zielzeit (F-28), aber ohne Zeit im Zeitraum: trotzdem zeigen - "0 von 5 h".
  const ohneZeit = [...areas.values()].filter((a) => !a.archived && (a.zielStundenWoche ?? 0) > 0 && !v.bereiche.some((b) => b.key === a.id));
  return [
    ...v.bereiche.map((b) => z({
      key: b.key, areaId: b.areaId, art: 'bereich', minuten: b.minuten,
      name: b.key === OHNE_BEREICH ? 'Ohne Bereich' : (areas.get(b.key)?.name ?? 'Unbekannter Bereich'),
      farbe: b.key === OHNE_BEREICH ? NO_AREA_COLOR : (areas.get(b.key)?.color ?? NO_AREA_COLOR),
    })),
    ...ohneZeit.map((a) => z({ key: a.id, areaId: a.id, art: 'bereich', minuten: 0, name: a.name, farbe: a.color })),
    z({ key: 'schlaf', art: 'schlaf', minuten: v.schlaf, name: v.schlafGeschaetzt === v.tage ? 'Schlaf (geschätzt)' : v.schlafGeschaetzt > 0 ? 'Schlaf (teils geschätzt)' : 'Schlaf', farbe: FARBE_SCHLAF }),
    z({ key: 'unverplant', art: 'unverplant', minuten: v.unverplant, name: 'Unverplant', farbe: FARBE_UNVERPLANT }),
  ];
}

/** Ring: die groessten Bereiche einzeln, der Rest als "Sonstiges". */
export function ringSegmente(liste: Zeile[]): RingSegment[] {
  const bereiche = liste.filter((z) => z.art === 'bereich');
  const rest = liste.filter((z) => z.art !== 'bereich');
  if (bereiche.length <= MAX_BEREICHE + 1) return [...bereiche, ...rest];
  const klein = bereiche.slice(MAX_BEREICHE);
  const minuten = klein.reduce((s, z) => s + z.minuten, 0);
  return [
    ...bereiche.slice(0, MAX_BEREICHE),
    { key: 'sonstiges', name: `Sonstiges (${klein.map((z) => z.name).join(', ')})`, farbe: FARBE_SONSTIGES, minuten, text: stunden(minuten) },
    ...rest,
  ];
}
